import { BookOpenText, ChevronDown, ChevronRight, Edit3, Plus, Trash2, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import ConfirmDialog from "../../components/ConfirmDialog.jsx";
import EmptyState from "../../components/EmptyState.jsx";
import { Field, Toggle } from "../../components/FormControls.jsx";
import Modal from "../../components/Modal.jsx";
import SearchBar from "../../components/SearchBar.jsx";
import { SkeletonList } from "../../components/Skeleton.jsx";
import { useToast } from "../../context/ToastContext.jsx";
import { deleteRecord, getMasterData, saveRecord } from "../../firebase/firestore.js";

const emptyClass = { name: "", active: true };
const emptyRoom = { roomNumber: "", active: true };

export default function ResourcesManager() {
  const [activeTab, setActiveTab] = useState("structure");
  const [master, setMaster] = useState(null);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({});
  const [modal, setModal] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sectionDrafts, setSectionDrafts] = useState({});
  const { showToast } = useToast();

  async function load(force = false) {
    setLoading(true);
    try {
      const data = await getMasterData(force);
      setMaster(data);
      setExpanded((current) => {
        if (Object.keys(current).length) return current;
        return Object.fromEntries((data.classes || []).slice(0, 1).map((item) => [item.id, true]));
      });
    } catch (error) {
      showToast(error.message || "Unable to load resources", "error");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const classRows = useMemo(() => {
    return (master?.classes || []).filter((classItem) => {
      const sections = (master?.sections || []).filter((section) => section.classId === classItem.id);
      const text = `${classItem.name} ${sections.map((section) => section.name).join(" ")}`.toLowerCase();
      return text.includes(search.toLowerCase());
    });
  }, [master, search]);

  const roomRows = useMemo(() => {
    return (master?.rooms || []).filter((room) => `${room.roomNumber}`.toLowerCase().includes(search.toLowerCase()));
  }, [master, search]);

  async function handleSaveModal(event) {
    event.preventDefault();
    setSaving(true);
    try {
      await saveRecord(modal.collectionName, modal.form, modal.id);
      showToast("Saved");
      setModal(null);
      await load(true);
    } catch (error) {
      showToast(error.message || "Unable to save", "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    setSaving(true);
    try {
      await deleteRecord(confirm.collectionName, confirm.id);
      showToast("Deleted");
      setConfirm(null);
      await load(true);
    } catch (error) {
      showToast(error.message || "Unable to delete", "error");
    } finally {
      setSaving(false);
    }
  }

  async function addSection(classId) {
    const name = sectionDrafts[classId]?.trim();
    if (!name) return;
    setSaving(true);
    try {
      await saveRecord("sections", { classId, name, active: true });
      setSectionDrafts((current) => ({ ...current, [classId]: "" }));
      showToast("Section added");
      await load(true);
    } catch (error) {
      showToast(error.message || "Unable to add section", "error");
    } finally {
      setSaving(false);
    }
  }

  function openClassForm(classItem = null) {
    setModal({ collectionName: "classes", id: classItem?.id || null, form: { ...emptyClass, ...(classItem || {}) } });
  }

  function openRoomForm(room = null) {
    setModal({ collectionName: "rooms", id: room?.id || null, form: { ...emptyRoom, ...(room || {}) } });
  }

  return (
    <section className="admin-page">
      <div className="page-title-row">
        <div>
          <p>Master Data</p>
          <h2>Class Structure / Rooms</h2>
        </div>
        <button
          className="btn btn-primary"
          type="button"
          onClick={() => (activeTab === "rooms" ? openRoomForm() : openClassForm())}
        >
          <Plus size={18} />
          {activeTab === "rooms" ? "Add Room" : "Add Class"}
        </button>
      </div>

      <div className="segmented two">
        <button className={activeTab === "structure" ? "active" : ""} type="button" onClick={() => setActiveTab("structure")}>
          Class Structure
        </button>
        <button className={activeTab === "rooms" ? "active" : ""} type="button" onClick={() => setActiveTab("rooms")}>
          Rooms
        </button>
      </div>

      <SearchBar value={search} onChange={setSearch} placeholder={activeTab === "rooms" ? "Search room" : "Search class or section"} />

      {loading ? (
        <SkeletonList />
      ) : activeTab === "structure" ? (
        <ClassStructure
          classes={classRows}
          sections={master?.sections || []}
          expanded={expanded}
          setExpanded={setExpanded}
          sectionDrafts={sectionDrafts}
          setSectionDrafts={setSectionDrafts}
          saving={saving}
          onAddSection={addSection}
          onEditClass={openClassForm}
          onDeleteClass={(item) => setConfirm({ ...item, collectionName: "classes" })}
          onEditSection={(item) => setModal({ collectionName: "sections", id: item.id, form: { classId: item.classId, name: item.name, active: item.active !== false } })}
          onDeleteSection={(item) => setConfirm({ ...item, collectionName: "sections" })}
        />
      ) : roomRows.length ? (
        <div className="manager-grid">
          {roomRows.map((room) => (
            <article className="card manager-card" key={room.id}>
              <div>
                <p className="eyebrow">{room.active === false ? "Inactive" : "Active"}</p>
                <h3>{room.roomNumber}</h3>
              </div>
              <div className="card-actions">
                <button className="icon-button" type="button" onClick={() => openRoomForm(room)}><Edit3 size={18} /></button>
                <button className="icon-button danger" type="button" onClick={() => setConfirm({ ...room, collectionName: "rooms" })}><Trash2 size={18} /></button>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <EmptyState title="No rooms found" text="Add rooms to use them in routine creation." />
      )}

      <Modal open={Boolean(modal)} title={modal?.id ? "Edit Record" : "Add Record"} onClose={() => setModal(null)}>
        <form className="form-grid" onSubmit={handleSaveModal}>
          {modal?.collectionName === "rooms" ? (
            <Field label="Room number">
              <input value={modal?.form.roomNumber || ""} onChange={(event) => setModal((m) => ({ ...m, form: { ...m.form, roomNumber: event.target.value } }))} required />
            </Field>
          ) : (
            <Field label={modal?.collectionName === "classes" ? "Class name" : "Section name"}>
              <input value={modal?.form.name || ""} onChange={(event) => setModal((m) => ({ ...m, form: { ...m.form, name: event.target.value } }))} required />
            </Field>
          )}
          <Toggle label="Active" checked={modal?.form.active !== false} onChange={(value) => setModal((m) => ({ ...m, form: { ...m.form, active: value } }))} />
          <div className="form-actions">
            <button className="btn btn-ghost" type="button" onClick={() => setModal(null)}>Cancel</button>
            <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? <span className="spinner" /> : null}Save</button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={Boolean(confirm)}
        title="Delete record?"
        message="This will remove the selected master record."
        loading={saving}
        onCancel={() => setConfirm(null)}
        onConfirm={handleDelete}
      />
    </section>
  );
}

function ClassStructure({
  classes,
  sections,
  expanded,
  setExpanded,
  sectionDrafts,
  setSectionDrafts,
  saving,
  onAddSection,
  onEditClass,
  onDeleteClass,
  onEditSection,
  onDeleteSection
}) {
  if (!classes.length) {
    return <EmptyState title="No classes found" text="Add classes, then open each class to add sections." />;
  }

  return (
    <article className="class-structure-card">
      <header className="class-structure-header">
        <div className="structure-icon"><BookOpenText size={28} /></div>
        <div>
          <h3>Class Structure</h3>
          <p>Define classes and add sections inside each class.</p>
        </div>
      </header>

      <div className="class-structure-list">
        {classes.map((classItem) => {
          const classSections = sections.filter((section) => section.classId === classItem.id);
          const isOpen = Boolean(expanded[classItem.id]);
          return (
            <section className={`class-row-card ${isOpen ? "open" : ""}`} key={classItem.id}>
              <div className="class-row-top">
                <button
                  className="class-expand-button"
                  type="button"
                  onClick={() => setExpanded((current) => ({ ...current, [classItem.id]: !current[classItem.id] }))}
                >
                  {isOpen ? <ChevronDown size={22} /> : <ChevronRight size={22} />}
                  <strong>{classItem.name}</strong>
                </button>
                <div className="card-actions">
                  <button className="icon-button" type="button" onClick={() => onEditClass(classItem)}><Edit3 size={18} /></button>
                  <button className="icon-button danger" type="button" onClick={() => onDeleteClass(classItem)}><Trash2 size={18} /></button>
                </div>
              </div>

              {isOpen ? (
                <div className="class-section-panel">
                  <h4>Sections</h4>
                  <div className="section-chip-input">
                    {classSections.map((section) => (
                      <span className="editable-chip" key={section.id}>
                        <button type="button" onClick={() => onEditSection(section)}>{section.name}</button>
                        <button type="button" onClick={() => onDeleteSection(section)} aria-label={`Delete ${section.name}`}>
                          <X size={16} />
                        </button>
                      </span>
                    ))}
                    <input
                      value={sectionDrafts[classItem.id] || ""}
                      placeholder="Add section (e.g., A, B, DAV)"
                      onChange={(event) => setSectionDrafts((current) => ({ ...current, [classItem.id]: event.target.value }))}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          onAddSection(classItem.id);
                        }
                      }}
                    />
                    <button className="btn btn-primary" type="button" disabled={saving} onClick={() => onAddSection(classItem.id)}>
                      <Plus size={17} />
                      Add
                    </button>
                  </div>
                </div>
              ) : null}
            </section>
          );
        })}
      </div>
    </article>
  );
}
